import { inject, Injectable, InjectionToken } from '@angular/core';

import { RealtimeSubscriberService } from '../core/realtime/realtime-subscriber.service';
import {
  RealtimeTelemetry,
  type RealtimeDurationBucket,
} from '../core/realtime/realtime-telemetry';
import { SessionService } from '../core/session/session.service';
import type { AuthoritativeTileResult, UtcDay } from './live-tile.model';
import { LiveTileStore } from './live-tile.store';

export type AuthoritativeTileRefresh = (
  signal: AbortSignal,
) => Promise<AuthoritativeTileResult>;

export type LiveTileDelay = (milliseconds: number, signal: AbortSignal) => Promise<void>;

export const LIVE_TILE_NOW = new InjectionToken<() => number>('LIVE_TILE_NOW', {
  factory: () => Date.now,
});

export const LIVE_TILE_DELAY = new InjectionToken<LiveTileDelay>('LIVE_TILE_DELAY', {
  factory: () => cancellableDelay,
});

const REFRESH_RETRY_DELAY_MILLISECONDS = 2_000;

@Injectable()
export class LiveTileViewService {
  private readonly subscriber = inject(RealtimeSubscriberService);
  private readonly store = inject(LiveTileStore);
  private readonly sessionService = inject(SessionService);
  private readonly now = inject(LIVE_TILE_NOW);
  private readonly delay = inject(LIVE_TILE_DELAY);
  private readonly telemetry = inject(RealtimeTelemetry);

  private consumerCount = 0;
  private generation = 0;
  private refreshController: AbortController | null = null;
  private rolloverController: AbortController | null = null;
  private releaseTransportRestored: (() => void) | null = null;
  private releaseClientSessionChanged: (() => void) | null = null;
  private activeDay: UtcDay | null = null;
  /**
   * Consumer callbacks in activation order; the FIRST entry owns reconnect,
   * rollover, and tenant-switch refreshes. On owner release with consumers
   * remaining, ownership transfers to the next active consumer — a released
   * view's callback is never invoked (contract §5).
   */
  private refreshCallbacks: AuthoritativeTileRefresh[] = [];

  activateCurrentDay(refresh: AuthoritativeTileRefresh): () => void {
    this.consumerCount += 1;
    this.refreshCallbacks.push(refresh);
    let released = false;

    if (this.consumerCount === 1) {
      const generation = ++this.generation;
      const day = utcDay(this.now());
      this.activeDay = day;
      this.releaseTransportRestored = this.subscriber.onTransportRestored((restoration) => {
        if (
          generation !== this.generation
          || this.consumerCount === 0
          || restoration.day !== this.activeDay
        ) {
          return;
        }
        const ownerRefresh = this.refreshCallbacks[0];
        if (ownerRefresh === undefined) return;
        this.store.requireAuthoritativeRefresh();
        this.startRefresh(generation, restoration.day, restoration.id);
      });
      // Tenant switch (FR-005): the subscriber cleared the store and restarted
      // the transport; re-establish this tenant's authoritative baseline.
      this.releaseClientSessionChanged = this.subscriber.onClientSessionChanged(() => {
        if (generation !== this.generation || this.consumerCount === 0) return;
        const day = this.activeDay;
        const ownerRefresh = this.refreshCallbacks[0];
        if (day === null || ownerRefresh === undefined) return;
        this.startRefresh(generation, day, null);
      });
      this.subscriber.activate(day);
      this.startRefresh(generation, day, null);
      this.scheduleRollover(generation);
    }

    return () => {
      if (released) return;
      released = true;
      this.consumerCount -= 1;
      // Remove exactly ONE registration for this activation — two consumers may
      // legitimately share one function reference, and filter would drop both.
      const callbackIndex = this.refreshCallbacks.indexOf(refresh);
      if (callbackIndex >= 0) this.refreshCallbacks.splice(callbackIndex, 1);
      if (this.consumerCount === 0) {
        this.generation += 1;
        this.refreshController?.abort();
        this.refreshController = null;
        this.rolloverController?.abort();
        this.rolloverController = null;
        this.releaseTransportRestored?.();
        this.releaseTransportRestored = null;
        this.releaseClientSessionChanged?.();
        this.releaseClientSessionChanged = null;
        this.activeDay = null;
        this.subscriber.deactivate();
        return;
      }
      // Ownership transfer (contract §5): the released consumer owned the
      // refresh callback; hand it to the next active consumer so rollover,
      // reconnect, and tenant-switch refreshes invoke a LIVE view. If the OWNER
      // (index 0) released with a refresh in flight, abort the removed owner's
      // request and restart with the new owner — a hung old request must not
      // leave the survivor without a baseline, and a late old result must never
      // be applied. A non-owner release needs no restart.
      const nextRefresh = this.refreshCallbacks[0];
      const day = this.activeDay;
      if (nextRefresh !== undefined && day !== null) {
        this.scheduleRollover(this.generation);
        if (callbackIndex === 0) {
          this.startRefresh(this.generation, day, null);
        }
      }
    };
  }

  private startRefresh(
    generation: number,
    day: UtcDay,
    restorationId: number | null,
  ): void {
    this.refreshController?.abort();
    const refreshController = new AbortController();
    this.refreshController = refreshController;
    void this.refreshUntilCurrent(
      generation,
      day,
      restorationId,
      refreshController.signal,
    );
  }

  private async refreshUntilCurrent(
    generation: number,
    day: UtcDay,
    restorationId: number | null,
    signal: AbortSignal,
  ): Promise<void> {
    while (this.isCurrent(generation, day, signal)) {
      // Resolve the callback per attempt (contract §5): ownership may transfer
      // mid-retry, so the loop must invoke the CURRENT owner, never a released
      // view's captured reference.
      const refresh = this.refreshCallbacks[0];
      if (refresh === undefined) return;
      const startedAt = performance.now();
      try {
        const result = await refresh(signal);
        if (!this.isCurrent(generation, day, signal)) return;
        const sessionClientCode = this.sessionService.session().clientCode;
        const currentClientCode = this.store.state().clientCode;
        if (
          result.day !== day
          // Fail closed (FR-003): without an authenticated client identity no
          // baseline is trustworthy — reject rather than accept any tenant.
          || sessionClientCode === null
          || result.clientCode !== sessionClientCode
          || (currentClientCode !== null && result.clientCode !== currentClientCode)
        ) {
          this.telemetry.authoritativeRefreshOutcome(
            'invalid-result',
            durationBucket(performance.now() - startedAt),
          );
          await this.delay(REFRESH_RETRY_DELAY_MILLISECONDS, signal);
          continue;
        }
        this.store.applyAuthoritative(result);
        this.telemetry.authoritativeRefreshOutcome(
          'succeeded',
          durationBucket(performance.now() - startedAt),
        );
        if (restorationId !== null) {
          this.subscriber.completeTransportRestoration(restorationId);
        }
        return;
      } catch {
        if (!this.isCurrent(generation, day, signal)) return;
        this.telemetry.authoritativeRefreshOutcome(
          'failed',
          durationBucket(performance.now() - startedAt),
        );
        try {
          await this.delay(REFRESH_RETRY_DELAY_MILLISECONDS, signal);
        } catch {
          return;
        }
      }
    }
  }

  private scheduleRollover(generation: number): void {
    this.rolloverController?.abort();
    const rolloverController = new AbortController();
    this.rolloverController = rolloverController;
    const milliseconds = nextUtcDayStart(this.now()) - this.now();
    void this.delay(milliseconds, rolloverController.signal)
      .then(() => {
        if (
          generation !== this.generation
          || this.consumerCount === 0
          || rolloverController.signal.aborted
        ) {
          return;
        }

        const day = utcDay(this.now());
        if (day === this.activeDay) {
          this.scheduleRollover(generation);
          return;
        }
        this.activeDay = day;
        this.store.beginUtcDay(day);
        this.subscriber.updateActiveDay(day);
        const restoration = this.subscriber.holdTransportUntilCurrent(day);
        this.startRefresh(generation, day, restoration?.id ?? null);
        this.scheduleRollover(generation);
      })
      .catch(() => undefined);
  }

  private isCurrent(generation: number, day: UtcDay, signal: AbortSignal): boolean {
    return generation === this.generation
      && this.consumerCount > 0
      && this.activeDay === day
      && !signal.aborted;
  }
}

function durationBucket(milliseconds: number): RealtimeDurationBucket {
  if (milliseconds < 250) return 'under-250ms';
  if (milliseconds < 1_000) return '250ms-to-1s';
  if (milliseconds < 5_000) return '1s-to-5s';
  return '5s-or-more';
}

function utcDay(now: number): UtcDay {
  return new Date(now).toISOString().slice(0, 10);
}

function nextUtcDayStart(now: number): number {
  const date = new Date(now);
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + 1,
  );
}

function cancellableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortReason(signal));
      return;
    }
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      clearTimeout(timeout);
      reject(abortReason(signal));
    };
    signal.addEventListener('abort', abort, { once: true });
  });
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Live tile work aborted.', 'AbortError');
}